import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { confirmDiscardChanges, hasFormChanged } from "../utils/unsavedChanges";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const normalizeEmail = (value) => (value || "")
  .toString()
  .replace(/[\u200B-\u200D\uFEFF]/g, "")
  .trim();

const onlyDigits = (value) => (value || "").toString().replace(/\D+/g, "");

const formatCPF = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length <= 3) return v;
  if (v.length <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`;
  if (v.length <= 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

const isValidCPF = (cpfRaw) => {
  const cpf = onlyDigits(cpfRaw);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;
  const calcCheck = (base) => {
    let sum = 0;
    let factor = base.length + 1;
    for (let i = 0; i < base.length; i++) sum += parseInt(base[i], 10) * factor--;
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calcCheck(cpf.slice(0, 9));
  const d2 = calcCheck(cpf.slice(0, 10));
  return d1 === parseInt(cpf[9], 10) && d2 === parseInt(cpf[10], 10);
};

const formatPhoneBR = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10)
    return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
};

const formatCEP = (value) => {
  const d = (value || "").toString().replace(/\D+/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
};

const normalizeUF = (value) => {
  if (!value) return value;
  return value.toString().trim().toUpperCase().slice(0, 2);
};

const composeLegacyAddress = (next) => {
  const street = (next.address_street || "").trim();
  const number = (next.address_number || "").trim();
  const complement = (next.address_complement || "").trim();
  const neighborhood = (next.address_neighborhood || "").trim();
  const city = (next.address_city || "").trim();
  const state = normalizeUF(next.address_state || "");
  const cep = (next.address_cep || "").trim();

  const line1 = [street, number].filter(Boolean).join(", ");
  const line2 = [complement, neighborhood].filter(Boolean).join(" - ");
  const line3 = [city, state].filter(Boolean).join(" - ");
  const line4 = cep ? `CEP ${cep}` : "";
  return [line1, line2, line3, line4].filter(Boolean).join(" / ");
};

export default function PatientForm({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  isEditing = false,
  title = "",
  canEditClinicalData = false,
}) {
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [searchingCep, setSearchingCep] = useState(false);
  const initialFormRef = useRef(formData);
  const overlayMouseDownRef = useRef(false);

  useEffect(() => {
    if (isOpen) initialFormRef.current = formData;
  }, [isOpen]);

  const requestClose = () => {
    const isDirty = hasFormChanged(initialFormRef.current, formData);
    if (confirmDiscardChanges(isDirty)) onClose();
  };

  const handleOverlayMouseDown = (event) => {
    overlayMouseDownRef.current = event.target === event.currentTarget;
  };

  const handleOverlayMouseUp = (event) => {
    if (overlayMouseDownRef.current && event.target === event.currentTarget) requestClose();
    overlayMouseDownRef.current = false;
  };

  useEffect(() => {
    if (!isOpen) setErrors({});
  }, [isOpen]);

  const validate = useMemo(
    () => (data) => {
      const v = {};

      if (!data.full_name || !data.full_name.trim()) v.full_name = "Nome é obrigatório";

      if (data.cpf) {
        if (!isValidCPF(data.cpf)) v.cpf = "CPF inválido";
      }

      if (data.legal_guardian_cpf) {
        if (!isValidCPF(data.legal_guardian_cpf)) v.legal_guardian_cpf = "CPF do responsável inválido";
      }

      if (data.email) {
        if (!EMAIL_REGEX.test(data.email)) v.email = "E-mail inválido";
      }

      return v;
    },
    []
  );

  const handleChange = (field) => (e) => {
    let value = e.target.value;

    if (["cpf", "legal_guardian_cpf"].includes(field)) value = formatCPF(value);
    if (["phone", "legal_guardian_phone", "emergency_contact_phone", "authorized_contact_phone"].includes(field)) {
      value = formatPhoneBR(value);
    }

    const addressFields = new Set([
      "address_street",
      "address_number",
      "address_complement",
      "address_neighborhood",
      "address_city",
      "address_state",
      "address_cep",
    ]);

    if (field === "address_state") value = normalizeUF(value);
    if (field === "address_cep") value = formatCEP(value);

    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      // Mantém compatibilidade com o campo legado "address"
      if (addressFields.has(field)) {
        const legacy = composeLegacyAddress(next);
        if (legacy) next.address = legacy;
      }

      return next;
    });

    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSearchCep = async () => {
    const cep = onlyDigits(formData.address_cep);

    if (cep.length !== 8) {
      window.alert("Informe um CEP válido com 8 dígitos.");
      return;
    }

    try {
      setSearchingCep(true);

      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (!response.ok || data?.erro) {
        window.alert("CEP não encontrado.");
        return;
      }

      setFormData((prev) => {
        const next = {
          ...prev,
          address_cep: formatCEP(cep),
          address_street: data.logradouro || prev.address_street || "",
          address_neighborhood: data.bairro || prev.address_neighborhood || "",
          address_city: data.localidade || prev.address_city || "",
          address_state: normalizeUF(data.uf || prev.address_state || ""),
        };

        next.address = composeLegacyAddress(next);

        return next;
      });
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      window.alert("Não foi possível buscar o CEP agora.");
    } finally {
      setSearchingCep(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const current = {
      ...formData,
      email: normalizeEmail(formData.email),
      cpf: formData.cpf ? formatCPF(formData.cpf) : "",
      phone: formData.phone ? formatPhoneBR(formData.phone) : "",
      address_state: formData.address_state ? normalizeUF(formData.address_state) : formData.address_state,
      address_cep: formData.address_cep ? formatCEP(formData.address_cep) : "",
    };

    const v = validate(current);
    setErrors(v);
    if (Object.values(v).some(Boolean)) return;

    try {
      setSubmitting(true);
      // CORREÇÃO CRÍTICA: envia o formData (não o evento)
      await onSubmit(current);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl mt-10">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {title || (isEditing ? "Editar Paciente" : "Novo Paciente")}
          </h2>
          <button type="button" onClick={requestClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome completo</label>
              <input
                type="text"
                value={formData.full_name || ""}
                onChange={handleChange("full_name")}
                className={`mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 ${
                  errors.full_name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.full_name && <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Data de nascimento</label>
              <input
                type="date"
                value={formData.birth_date || ""}
                onChange={handleChange("birth_date")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">CPF</label>
              <input
                type="text"
                value={formData.cpf ? formatCPF(formData.cpf) : ""}
                onChange={handleChange("cpf")}
                placeholder="000.000.000-00"
                className={`mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 ${
                  errors.cpf ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.cpf && <p className="mt-1 text-sm text-red-600">{errors.cpf}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">RG / Documento complementar</label>
              <input
                type="text"
                value={formData.rg || ""}
                onChange={handleChange("rg")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Telefone</label>
              <input
                type="text"
                value={formData.phone ? formatPhoneBR(formData.phone) : ""}
                onChange={handleChange("phone")}
                placeholder="(00) 00000-0000"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">E-mail</label>
              <input
                type="email"
                value={formData.email || ""}
                onChange={handleChange("email")}
                placeholder="nome@exemplo.com"
                className={`mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Gênero</label>
              <select
                value={formData.gender || ""}
                onChange={handleChange("gender")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="">Selecione</option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
          </div>

          {/* Endereço estruturado + legado */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Endereço (Tomador)</label>
            <p className="text-xs text-gray-500 mt-1">
              Preencha os campos separados para a NFS-e/DANFSe. O campo “Endereço (texto único)” é mantido por compatibilidade.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Logradouro</label>
                <input
                  type="text"
                  value={formData.address_street || ""}
                  onChange={handleChange("address_street")}
                  placeholder="Rua / Avenida"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nº</label>
                <input
                  type="text"
                  value={formData.address_number || ""}
                  onChange={handleChange("address_number")}
                  placeholder="Número"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Complemento</label>
                <input
                  type="text"
                  value={formData.address_complement || ""}
                  onChange={handleChange("address_complement")}
                  placeholder="Apto, sala, etc."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Bairro</label>
                <input
                  type="text"
                  value={formData.address_neighborhood || ""}
                  onChange={handleChange("address_neighborhood")}
                  placeholder="Bairro"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Município</label>
                <input
                  type="text"
                  value={formData.address_city || ""}
                  onChange={handleChange("address_city")}
                  placeholder="Cidade"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">UF</label>
                <input
                  type="text"
                  value={formData.address_state || ""}
                  onChange={handleChange("address_state")}
                  placeholder="UF"
                  maxLength={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">CEP</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.address_cep || ""}
                    onChange={handleChange("address_cep")}
                    placeholder="00000-000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    type="button"
                    onClick={handleSearchCep}
                    disabled={searchingCep}
                    className="px-3 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-60 whitespace-nowrap"
                  >
                    {searchingCep ? "Buscando..." : "Buscar"}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Endereço (texto único)</label>
                <input
                  type="text"
                  value={formData.address || ""}
                  onChange={handleChange("address")}
                  placeholder="Rua, número, complemento, bairro, cidade/UF"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Restante do formulário permanece como já estava */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Estado civil</label>
              <select
                value={formData.marital_status || ""}
                onChange={handleChange("marital_status")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="">Selecione</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
                <option value="União Estável">União Estável</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Profissão</label>
              <input
                type="text"
                value={formData.profession || ""}
                onChange={handleChange("profession")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Contato de emergência</label>
              <input
                type="text"
                value={formData.emergency_contact_name || ""}
                onChange={handleChange("emergency_contact_name")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                placeholder="Nome do contato"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Telefone do contato de emergência</label>
              <input
                type="text"
                value={formData.emergency_contact_phone ? formatPhoneBR(formData.emergency_contact_phone) : ""}
                onChange={handleChange("emergency_contact_phone")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                placeholder="(81) 99999-9999"
              />
            </div>

            <div className="md:col-span-2 border rounded-xl p-4 bg-amber-50">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={!!formData.is_minor_or_dependent}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, is_minor_or_dependent: e.target.checked }))
                  }
                />
                Paciente possui responsável legal / representante?
              </label>
            </div>

            {formData.is_minor_or_dependent && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tipo de responsável / representação
                  </label>
                  <select
                    value={formData.legal_guardian_type || ""}
                    onChange={handleChange("legal_guardian_type")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">Selecione</option>
                    <option value="menor">Menor de idade</option>
                    <option value="tutor">Tutor(a) / guardião(ã)</option>
                    <option value="curador">Curador(a)</option>
                    <option value="procurador">Procurador(a)</option>
                    <option value="incapaz">Responsável por paciente incapaz</option>
                    <option value="acompanhante_autorizado">Responsável/acompanhante autorizado</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>              
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome do responsável legal</label>
                  <input
                    type="text"
                    value={formData.legal_guardian_name || ""}
                    onChange={handleChange("legal_guardian_name")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">CPF do responsável legal</label>
                  <input
                    type="text"
                    value={formData.legal_guardian_cpf || ""}
                    onChange={handleChange("legal_guardian_cpf")}
                    className={`mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 ${
                      errors.legal_guardian_cpf ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.legal_guardian_cpf && (
                    <p className="mt-1 text-sm text-red-600">{errors.legal_guardian_cpf}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefone do responsável legal</label>
                  <input
                    type="text"
                    value={formData.legal_guardian_phone ? formatPhoneBR(formData.legal_guardian_phone) : ""}
                    onChange={handleChange("legal_guardian_phone")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Parentesco / vínculo</label>
                  <input
                    type="text"
                    value={formData.legal_guardian_relationship || ""}
                    onChange={handleChange("legal_guardian_relationship")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex.: mãe, pai, tutor(a), curador(a)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base da representação
                  </label>
                  <input
                    type="text"
                    value={formData.legal_guardian_basis || ""}
                    onChange={handleChange("legal_guardian_basis")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex.: poder familiar, guarda, tutela, curatela, procuração"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Observações sobre o responsável/representante
                  </label>
                  <textarea
                    value={formData.legal_guardian_notes || ""}
                    onChange={handleChange("legal_guardian_notes")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    rows={2}
                    placeholder="Ex.: documento apresentado, limitação de poderes, observações administrativas"
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2 border rounded-xl p-4 bg-slate-50">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Dados complementares do paciente
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nome social / nome preferido
                  </label>
                  <input
                    type="text"
                    value={formData.preferred_name || ""}
                    onChange={handleChange("preferred_name")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Como o paciente prefere ser chamado"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Pessoa autorizada a receber informações
                  </label>
                  <input
                    type="text"
                    value={formData.authorized_contact_name || ""}
                    onChange={handleChange("authorized_contact_name")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Nome da pessoa autorizada"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Telefone da pessoa autorizada
                  </label>
                  <input
                    type="text"
                    value={formData.authorized_contact_phone ? formatPhoneBR(formData.authorized_contact_phone) : ""}
                    onChange={handleChange("authorized_contact_phone")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Vínculo / parentesco
                  </label>
                  <input
                    type="text"
                    value={formData.authorized_contact_relationship || ""}
                    onChange={handleChange("authorized_contact_relationship")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex.: mãe, filho, cônjuge, cuidador"
                  />
                </div>

                {canEditClinicalData && <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Necessidades de acessibilidade
                  </label>
                  <textarea
                    value={formData.accessibility_needs || ""}
                    onChange={handleChange("accessibility_needs")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    rows={2}
                    placeholder="Ex.: deficiência auditiva, visual, mobilidade reduzida, TEA, necessidade de acompanhante"
                  />
                </div>}

                {canEditClinicalData && <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Diretivas antecipadas de vontade
                  </label>
                  <textarea
                    value={formData.advance_directives || ""}
                    onChange={handleChange("advance_directives")}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    rows={2}
                    placeholder="Informe se o paciente possui diretiva antecipada, representante indicado ou observação relevante"
                  />
                </div>}
              </div>
            </div>

            {canEditClinicalData && <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Alertas clínicos importantes</label>
              <textarea
                value={formData.clinical_alerts || ""}
                onChange={handleChange("clinical_alerts")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="Ex.: alergias graves, condição sistêmica relevante, uso de anticoagulante, necessidade de cuidado especial"
              />
            </div>}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Observações administrativas</label>
              <textarea
                value={formData.administrative_notes || ""}
                onChange={handleChange("administrative_notes")}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                rows={3}
              />
            </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={requestClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                submitting ? "bg-purple-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700"
              }`}
            >
              {submitting ? (isEditing ? "Salvando..." : "Cadastrando...") : isEditing ? "Salvar alterações" : "Cadastrar Paciente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
